import { stripFundDtoUnsupportedFields } from '@/modules/fund/domain/fundAdapter';
import type {
  FundFormValues,
  InvestmentFundPhase,
  InvestmentFundRecord,
} from '@/modules/fund/types/fund';
import type {
  CreateFundSetupMatterInput,
  CreateFundSetupVoteInput,
  FundGovernanceMember,
  FundSetupMatter,
  FundSetupProgress,
  FundSetupStage,
  FundSetupVoteRecord,
  UpdateFundSetupMatterInput,
} from '@/modules/fund/types/fundSetup';
import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const BASE_PATH = '/bff/investment/funds';
/** 投资流程逐人投票沿用事项挂载路径（与基金详情同源）。 */
const FUND_SETUP_BASE_PATH = '/bff/investment/fund-setup';

export type FundAuditResponse = {
  lifecycle: Array<{
    uuid: string;
    action: string;
    afterJson?: Record<string, unknown> | null;
    createdAt: string;
  }>;
  setup: Array<{
    uuid: string;
    stage: string;
    title: string;
    status: string;
    result?: string | null;
    filledAt?: string | null;
  }>;
};

export type FundOperation =
  | 'opportunity'
  | 'commitment'
  | 'project'
  | 'closing'
  | 'capitalCall'
  | 'firstClose'
  | 'finalClose'
  | 'distribution';
export type FundOperationReadiness = {
  fundUuid: string;
  asOf: string;
  operations: Record<
    FundOperation,
    { allowed: boolean; blockers: string[]; warnings: string[] }
  >;
};

export type FundFirstCloseReadiness = {
  fundUuid: string;
  phase: InvestmentFundPhase;
  firstCloseAt: string | null;
  commitmentCount: number;
  ready: boolean;
  gaps: Array<{ code: string; label: string; detail?: string }>;
};
export type FundFinalCloseReadiness = FundFirstCloseReadiness & {
  finalCloseAt: string | null;
  committedAmount: string;
  targetAmount: string | null;
};

/** 我方出资汇总（来自认缴与 capital_call 现金流）。 */
export type FundOurContributionSummary = {
  fundUuid: string;
  basisStatus?: 'ready' | 'missing_subject' | 'missing_identity';
  ourShareRatio?: number | null;
  ourOrgUuids: string[];
  committed: number | null;
  paidIn: number | null;
  unassignedPaidIn?: number;
  effectivePaidIn?: number | null;
  outstanding: number | null;
  earliestPaidAt: string | null;
};

/** 组织树节点：公司=根节点、部门=子节点、小组=孙节点，最多 4 层。 */
export type FundOrgNode = {
  id: string;
  name: string;
  children?: FundOrgNode[];
};

/** 组织树接口返回；IAM 失败或空树时后端返回 { nodes: [] }，前端据此降级为文本输入。 */
export type FundOrgTreeResponse = {
  nodes: FundOrgNode[];
};

export const fundApi = {
  operationReadiness: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<FundOperationReadiness>(
      `${BASE_PATH}/${uuid}/operation-readiness`,
      opts,
    ),
  listFunds: (params?: { controlType?: '自管' | '参股' }) =>
    request<InvestmentFundRecord[]>(BASE_PATH, {
      method: 'GET',
      params,
    }),

  getFund: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}`, {
      method: 'GET',
      ...opts,
    }),

  /** 当前租户组织树（所属公司/部门/小组级联下拉数据源）。 */
  getOrgTree: () =>
    request<FundOrgTreeResponse>(`${BASE_PATH}/org-tree`, {
      method: 'GET',
    }),

  /** 我方实缴 / 待实缴 / 最早出资日期（来自 LP 认缴与实缴现金流汇总）。 */
  ourContribution: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<FundOurContributionSummary>(
      `${BASE_PATH}/${uuid}/investor-cash-summary/our`,
      { method: 'GET', ...opts },
    ),

  /** 备案联动提醒（G-05）。 */
  filingReminder: (uuid: string) =>
    request<{
      fundUuid: string;
      registrationType: string | null;
      required: boolean;
      filingConfirmed: boolean;
      filingNo: string | null;
      reminder: string | null;
    }>(`${BASE_PATH}/${uuid}/filing-reminder`, {
      method: 'GET',
    }),

  audit: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<FundAuditResponse>(`${BASE_PATH}/${uuid}/audit`, {
      method: 'GET',
      ...opts,
    }),

  /** 首关就绪清单：确认首关前仍缺的合规项。 */
  firstCloseReadiness: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<FundFirstCloseReadiness>(`${BASE_PATH}/${uuid}/first-close-readiness`, {
      method: 'GET',
      ...opts,
    }),
  finalCloseReadiness: (uuid: string, opts?: { skipErrorHandler?: boolean }) =>
    request<FundFinalCloseReadiness>(`${BASE_PATH}/${uuid}/final-close-readiness`, {
      method: 'GET',
      ...opts,
    }),

  createFund: (payload: FundFormValues) =>
    request<InvestmentFundRecord>(BASE_PATH, {
      method: 'POST',
      data: stripFundDtoUnsupportedFields({
        currency: 'CNY',
        ...payload,
      } as FundFormValues),
    }),

  updateFund: (uuid: string, payload: Partial<FundFormValues>) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}`, {
      method: 'PATCH',
      data: stripFundDtoUnsupportedFields(payload),
    }),

  /**
   * 受控配置 GP 收益受信主体（issue 34）：募集后该字段不能经普通档案 PATCH 改写，
   * 必须走本接口并填写配置依据。
   */
  configureGpCarryLpOrg: (
    uuid: string,
    gpCarryLpOrgUuid: string,
    basis: string,
  ) =>
    request<InvestmentFundRecord>(`${BASE_PATH}/${uuid}/gp-carry-lp`, {
      method: 'PATCH',
      data: { gpCarryLpOrgUuid, basis },
    }),

  /** 基金详情直接维护投资流程。 */
  setupMatters: (uuid: string, stage?: FundSetupStage) =>
    request<FundSetupMatter[]>(`${BASE_PATH}/${uuid}/setup/matters`, {
      method: 'GET',
      params: stage ? { stage } : undefined,
    }),

  getSetupProgress: (uuid: string) =>
    request<FundSetupProgress | null>(`${BASE_PATH}/${uuid}/setup/progress`, {
      method: 'GET',
    }),

  listSetupMatters: (uuid: string) =>
    request<FundSetupMatter[]>(`${BASE_PATH}/${uuid}/setup/matters`, {
      method: 'GET',
    }),

  createSetupMatter: (uuid: string, data: CreateFundSetupMatterInput) =>
    request<FundSetupMatter>(`${BASE_PATH}/${uuid}/setup/matters`, {
      method: 'POST',
      data,
    }),

  updateSetupMatter: (
    uuid: string,
    matterUuid: string,
    data: UpdateFundSetupMatterInput,
  ) =>
    request<FundSetupMatter>(`${BASE_PATH}/${uuid}/setup/matters/${matterUuid}`, {
      method: 'PATCH',
      data,
    }),

  deleteSetupMatter: (uuid: string, matterUuid: string) =>
    request(`${BASE_PATH}/${uuid}/setup/matters/${matterUuid}`, {
      method: 'DELETE',
    }),

  withdrawSetupMatterApproval: (uuid: string, matterUuid: string) =>
    request(`${BASE_PATH}/${uuid}/setup/matters/${matterUuid}/approval/withdraw`, { method: 'POST' }),

  resubmitSetupMatterApproval: (uuid: string, matterUuid: string) =>
    request(`${BASE_PATH}/${uuid}/setup/matters/${matterUuid}/approval/resubmit`, { method: 'POST' }),

  completeSetupMatter: (
    uuid: string,
    matterUuid: string,
    options?: {
      advanceStage?: boolean;
      bypassPrerequisites?: boolean;
      bypassReason?: string;
    } | boolean,
  ) => {
    const data =
      typeof options === 'boolean'
        ? { advanceStage: options }
        : { advanceStage: true, ...options };
    return request<{
      matter: FundSetupMatter;
      progress: FundSetupProgress;
      /** 协议签订完成时后端自动生成的协议版本草稿；生效仍走协议版本页。 */
      termsDraft?: { uuid: string; version: number };
    }>(`${BASE_PATH}/${uuid}/setup/matters/${matterUuid}/complete`, {
      method: 'POST',
      data,
    });
  },

  listGovernanceMembers: (uuid: string) =>
    request<FundGovernanceMember[]>(`${BASE_PATH}/${uuid}/governance-members`, {
      method: 'GET',
    }),

  replaceGovernanceMembers: (
    uuid: string,
    members: Array<
      Omit<FundGovernanceMember, 'uuid' | 'fundUuid' | 'sortOrder'> & {
        sortOrder?: number;
      }
    >,
  ) =>
    request<FundGovernanceMember[]>(`${BASE_PATH}/${uuid}/governance-members`, {
      method: 'PUT',
      data: { members },
    }),

  /** 投决逐人投票记录（挂在基金投资流程事项下）。 */
  listSetupVotes: (matterUuid: string) =>
    request<FundSetupVoteRecord[]>(
      `${FUND_SETUP_BASE_PATH}/matters/${matterUuid}/votes`,
      {
        method: 'GET',
      },
    ),

  createSetupVote: (matterUuid: string, data: CreateFundSetupVoteInput) =>
    request<FundSetupVoteRecord>(
      `${FUND_SETUP_BASE_PATH}/matters/${matterUuid}/votes`,
      {
        method: 'POST',
        data,
      },
    ),

  deleteSetupVote: (matterUuid: string, voteUuid: string) =>
    request(`${FUND_SETUP_BASE_PATH}/matters/${matterUuid}/votes/${voteUuid}`, {
      method: 'DELETE',
    }),

  /** 首关与终关是独立业务事实，后端按同一规则核验并确认。 */
  confirmFirstClose: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(
      `${BASE_PATH}/${uuid}/lifecycle/first-close`,
      { method: 'POST', data: { remark } },
    ),
  confirmFinalClose: (uuid: string, remark?: string) =>
    request<InvestmentFundRecord>(
      `${BASE_PATH}/${uuid}/lifecycle/final-close`,
      { method: 'POST', data: { remark } },
    ),
  /** 软删除基金；仅允许已清算完成的基金才能删除。 */
  softDelete: (uuid: string) =>
    request<void>(`${BASE_PATH}/${uuid}`, {
      method: 'DELETE',
    }),

  /** 退出回款待分配列表。 */
  listPendingDistributions: (params?: { fundUuid?: string }) =>
    request<
      Array<{
        id: string;
        fundUuid: string;
        exitTransactionUuid: string;
        portfolioCompanyUuid: string;
        grossProceeds: number;
        netProceeds: number;
        realizedGain: number;
        status: string;
        completedAt?: string;
        allowedNextStatuses?: string[];
      }>
    >(`${BASE_PATH}/pending-distributions`, {
      method: 'GET',
      params,
    }),

  /** 推进待分配状态。 */
  updatePendingDistributionStatus: (
    uuid: string,
    data: { status: '待分配' | '已分配' | '已取消'; remark?: string },
  ) =>
    request(`${BASE_PATH}/pending-distributions/${uuid}/status`, {
      method: 'PATCH',
      data,
    }),
};
